<script setup lang="ts">
  import type { ScrollAreaRootProps } from 'reka-ui'
  import type { HTMLAttributes } from 'vue'
  import { reactiveOmit } from '@vueuse/core'
  import { ScrollAreaCorner, ScrollAreaRoot, ScrollAreaViewport } from 'reka-ui'
  import { cn } from '@rapoo/element/lib/utils'
  import ScrollBar from './ScrollBar.vue'

  const props = withDefaults(
    defineProps<
      ScrollAreaRootProps & {
        class?: HTMLAttributes['class']
        scrollbarClass?: HTMLAttributes['class']
        thumbClass?: HTMLAttributes['class']
        size?: 'sm' | 'md' | 'lg'
        scrollbarOrientation?: 'vertical' | 'horizontal' | 'both'
        wheelBehavior?: 'native' | 'horizontal'
      }
    >(),
    {
      size: 'md',
      scrollbarOrientation: 'vertical',
      wheelBehavior: 'native',
    },
  )

  const delegatedProps = reactiveOmit(
    props,
    'class',
    'scrollbarClass',
    'thumbClass',
    'size',
    'scrollbarOrientation',
    'wheelBehavior',
  )

  /** 将鼠标纵向滚轮转换为横向滚动；到达边界后保留页面的纵向滚动。 */
  function onWheel(event: WheelEvent) {
    if (props.wheelBehavior !== 'horizontal') return

    const viewport = event.currentTarget as HTMLElement
    if (viewport.scrollWidth <= viewport.clientWidth) return

    const rawDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
    if (rawDelta === 0) return

    const unit = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16 : viewport.clientWidth
    const delta = event.deltaMode === WheelEvent.DOM_DELTA_PIXEL ? rawDelta : rawDelta * unit
    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth
    const nextScrollLeft = Math.min(maxScrollLeft, Math.max(0, viewport.scrollLeft + delta))

    if (nextScrollLeft === viewport.scrollLeft) return
    viewport.scrollLeft = nextScrollLeft
    event.preventDefault()
  }
</script>

<template>
  <ScrollAreaRoot v-bind="delegatedProps" :class="cn('relative overflow-hidden', props.class)">
    <ScrollAreaViewport class="h-full w-full" @wheel="onWheel">
      <slot />
    </ScrollAreaViewport>
    <ScrollBar
      v-if="scrollbarOrientation === 'vertical' || scrollbarOrientation === 'both'"
      :size="size"
      orientation="vertical"
      :class="scrollbarClass"
      :thumb-class="thumbClass"
    />
    <ScrollBar
      v-if="scrollbarOrientation === 'horizontal' || scrollbarOrientation === 'both'"
      :size="size"
      orientation="horizontal"
      :class="scrollbarClass"
      :thumb-class="thumbClass"
    />
    <ScrollAreaCorner />
  </ScrollAreaRoot>
</template>
